import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type {
  ConnectorPinView,
  CreateInstanceBody,
  CreateInstanceResponse,
  DisableInstanceBody,
  EnableInstanceBody,
  InstanceAdminView,
  MarkStorageRemovedBody,
  MarkStorageRemovedResponse,
  ProvisioningJobView,
  ProvisioningStatus,
  StorageRemovalCheck,
  UpdateInstanceBody,
} from "@shared/types/instancesAdmin";
import { ApiClientError, apiRequest } from "@/lib/api";
import { listQueryOptions } from "@/lib/listQuery";
import { ME_QUERY_KEY } from "@/hooks/use-me";

/**
 * Instance administration (FR-20, FR-22, FR-32).
 *
 * Creating an instance is asynchronous: the route answers 202 with a
 * provisioning job, because the tenant database does not exist yet when the
 * response is written. Everything below exists to make that visible — the list
 * shows how far along a provisioning row is, and the job poll stops the moment
 * the job is terminal rather than running for ever on a screen nobody closed.
 */
export const INSTANCES_ADMIN_PATH = "/api/v2/platform/instances";

export const INSTANCES_ADMIN_QUERY_KEY = [INSTANCES_ADMIN_PATH] as const;

/** How often a live provisioning job is re-read (P3 on the canvas: every 3 s). */
export const PROVISIONING_POLL_INTERVAL_MS = 3000;

const TERMINAL_STATUSES: ReadonlySet<ProvisioningStatus> = new Set(["succeeded", "failed"]);

/**
 * The key a disabled detail query parks on.
 *
 * A TanStack query with `enabled: false` still READS its cache key, so a detail
 * hook that fell back to a list's key while it waited for an id would hand the
 * caller that list and type it as one row. These sentinels are strings nothing
 * ever writes, so a disabled query resolves to `undefined` — which is what "no
 * id yet" means. `platformAdminHooks.keys.test.ts` asserts they collide with no
 * real path.
 */
export const PROVISIONING_DISABLED_KEY = "provisioning-disabled";
export const CONNECTOR_PIN_DISABLED_KEY = "connector-pin-disabled";
export const STORAGE_REMOVAL_CHECK_DISABLED_KEY = "storage-removal-check-disabled";

export function isTerminalProvisioning(status: ProvisioningStatus | undefined): boolean {
  return status !== undefined && TERMINAL_STATUSES.has(status);
}

export function provisioningPath(instanceId: string): string {
  return `${INSTANCES_ADMIN_PATH}/${instanceId}/provisioning`;
}

export function useInstancesAdmin() {
  return useQuery<InstanceAdminView[]>({
    ...listQueryOptions,
    queryKey: INSTANCES_ADMIN_QUERY_KEY,
  });
}

/**
 * An instance write changes what `GET /me` lists (B-08): a new instance can be entered, a rename
 * shows in the top bar and the picker, a disabled one leaves them. So `/me` is read again with the
 * admin list.
 */
function invalidateAfterInstanceWrite(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: INSTANCES_ADMIN_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  ]);
}

export function useCreateInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInstanceBody) =>
      apiRequest<CreateInstanceResponse>(INSTANCES_ADMIN_PATH, { method: "POST", body }),
    onSuccess: () => invalidateAfterInstanceWrite(queryClient),
  });
}

export function useUpdateInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateInstanceBody }) =>
      apiRequest<InstanceAdminView>(`${INSTANCES_ADMIN_PATH}/${id}`, { method: "PUT", body }),
    onSuccess: () => invalidateAfterInstanceWrite(queryClient),
  });
}

export function useDisableInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DisableInstanceBody }) =>
      apiRequest<InstanceAdminView>(`${INSTANCES_ADMIN_PATH}/${id}/disable`, {
        method: "POST",
        body,
        // #1110: disable, enable and mark storage removed need a recent sign-in.
        stepUp: "change",
      }),
    onSuccess: () => invalidateAfterInstanceWrite(queryClient),
  });
}

/** Round 9 (B-15): the way back from Disable; the schedules stay off. */
export function useEnableInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: EnableInstanceBody }) =>
      apiRequest<InstanceAdminView>(`${INSTANCES_ADMIN_PATH}/${id}/enable`, {
        method: "POST",
        body,
        stepUp: "change",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INSTANCES_ADMIN_QUERY_KEY }),
  });
}

/**
 * F36: `POST /platform/instances/:id/provisioning/retry` requeues the instance's newest failed
 * provisioning job (202), or answers 409 while one is queued or running or the newest did not
 * fail. Either way the job and the list are read again, so the panel starts polling the new run.
 */
export function useRetryInstanceProvisioning() {
  const queryClient = useQueryClient();
  return useMutation({
    // A disabled instance's Retry enables it too, so it carries a reason (the route refuses none).
    mutationFn: ({ instanceId, reason }: { instanceId: string; reason?: string }) =>
      apiRequest<ProvisioningJobView>(`${INSTANCES_ADMIN_PATH}/${instanceId}/provisioning/retry`, {
        method: "POST",
        body: reason ? { reason } : {},
      }),
    onSettled: (_data, _error, { instanceId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [provisioningPath(instanceId)] }),
        invalidateAfterInstanceWrite(queryClient),
      ]),
  });
}

/**
 * The mark's preflight (`GET /platform/instances/:id/storage-removal-check`): whether the
 * write-once archive holds the instance's audit chain. Read fresh each time the dialog opens.
 */
export function storageRemovalCheckPath(instanceId: string): string {
  return `${INSTANCES_ADMIN_PATH}/${instanceId}/storage-removal-check`;
}

export function useStorageRemovalCheck(instanceId: string | null) {
  return useQuery<StorageRemovalCheck>({
    queryKey: [
      instanceId ? storageRemovalCheckPath(instanceId) : STORAGE_REMOVAL_CHECK_DISABLED_KEY,
    ],
    enabled: Boolean(instanceId),
    staleTime: 0,
    retry: false,
  });
}

/**
 * Marks a disabled instance whose database or schema is gone (platform 0033). The answer says
 * whether its audit history was archived (`archive`).
 */
export function useMarkStorageRemoved() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MarkStorageRemovedBody }) =>
      apiRequest<MarkStorageRemovedResponse>(`${INSTANCES_ADMIN_PATH}/${id}/mark-storage-removed`, {
        method: "POST",
        body,
        stepUp: "change",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INSTANCES_ADMIN_QUERY_KEY }),
  });
}

/** A 404 (no job) or a 403 (not this caller's to read) will answer the same next time. */
function isRefusedForGood(error: unknown): boolean {
  return error instanceof ApiClientError && (error.status === 404 || error.status === 403);
}

/**
 * One instance's latest provisioning job, re-read every three seconds until it
 * reaches a terminal state.
 *
 * The interval is a function of the CURRENT data rather than of a flag the
 * caller keeps in sync: a boolean prop would have to be recomputed by whoever
 * owns the component, and the one time it was not the poll would carry on for
 * as long as the tab stayed open. Returning `false` from the callback is the
 * only place the decision lives.
 *
 * A 404 is not retried: an instance that was created before this release, or
 * one whose job row was never written, has no job and will not grow one. A 403 is not retried
 * either, and stops the poll: the route is platform scope, and a caller refused once is refused
 * again (B-10; the screen does not ask at all unless the caller holds the permission).
 */
export function useProvisioningJob(instanceId: string | null) {
  const queryClient = useQueryClient();
  const job = useQuery<ProvisioningJobView>({
    queryKey: [instanceId ? provisioningPath(instanceId) : PROVISIONING_DISABLED_KEY],
    enabled: Boolean(instanceId),
    refetchInterval: (query) =>
      isTerminalProvisioning(query.state.data?.status) || isRefusedForGood(query.state.error)
        ? false
        : PROVISIONING_POLL_INTERVAL_MS,
    retry: (failureCount, error) => !isRefusedForGood(error) && failureCount < 2,
    staleTime: 0,
  });
  // B-08: a job that finishes while watched changes what `/me` lists (a provisioned instance can
  // be entered), so `/me` is read again once, on the move to a terminal state. #930: so is the
  // admin list, whose row still says "provisioning" until it is read again (the job activates the
  // instance as its last step), so the grid's status turns Active, or failed, with no reload.
  const status = job.data?.status;
  const seen = useRef(status);
  useEffect(() => {
    const before = seen.current;
    seen.current = status;
    if (before !== undefined && !isTerminalProvisioning(before) && isTerminalProvisioning(status)) {
      void invalidateAfterInstanceWrite(queryClient);
    }
  }, [status, queryClient]);
  return job;
}

/* --------------------------------------------------------------------------------- *
 * Connector tag pins (FR-32)
 * --------------------------------------------------------------------------------- */

export function connectorPinPath(instanceId: string, connectorName: string): string {
  return `${INSTANCES_ADMIN_PATH}/${instanceId}/connector-pins/${connectorName}`;
}

/**
 * The pinned tag for one instance and one connector, or null when it runs the
 * definition's default.
 *
 * "No pin" is a 404 from the route, which is the correct answer to "show me the
 * pin" and the wrong thing to show a user as an error — so it is translated to
 * `null` here, once, rather than in each of the callers that would otherwise
 * have to know that a missing row and a broken request look the same.
 */
export function useConnectorPin(instanceId: string, connectorName: string | null) {
  const path = connectorName ? connectorPinPath(instanceId, connectorName) : null;
  return useQuery<ConnectorPinView | null>({
    queryKey: [path ?? CONNECTOR_PIN_DISABLED_KEY],
    enabled: Boolean(path),
    queryFn: async ({ signal }) => {
      try {
        return await apiRequest<ConnectorPinView>(path as string, { signal });
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 404) return null;
        throw error;
      }
    },
    retry: false,
  });
}

export function useSetConnectorPin(connectorName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ instanceId, tag }: { instanceId: string; tag: string }) =>
      apiRequest<ConnectorPinView>(connectorPinPath(instanceId, connectorName), {
        method: "PUT",
        body: { tag },
      }),
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: [connectorPinPath(variables.instanceId, connectorName)],
      }),
  });
}

export function useClearConnectorPin(connectorName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ instanceId }: { instanceId: string }) =>
      apiRequest<void>(connectorPinPath(instanceId, connectorName), { method: "DELETE" }),
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: [connectorPinPath(variables.instanceId, connectorName)],
      }),
  });
}
